import React, { memo } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { STEP_STATUS } from '../../../constants/stepStatus';

/**
 * StepButton — renders a clickable button for each step in the indicator.
 *
 * Features:
 * - Shows a circle (or custom children) representing the step.
 * - Applies a white ring and shadow when current.
 * - Applies a muted border for upcoming.
 * - Disables click when not allowed.
 * - Exposes ARIA attributes for accessibility.
 *
 * @param {Object} props
 * @param {STEP_STATUS} props.status       — CURRENT, COMPLETED or UPCOMING
 * @param {string}      props.label        — text label under the icon
 * @param {boolean}     props.isClickable  — whether the user can click this step
 * @param {function}    props.onClick      — callback when step is clicked
 * @param {string}      [props.buttonClass]— extra classes on the <button>
 * @param {string}      [props.labelClass] — extra classes on the label <span>
 * @param {React.ReactNode} [props.children] — overrides the default circle content
 */
const StepButton = memo(
  ({ status, label, isClickable, onClick, buttonClass = '', labelClass = '', children }) => {
    const isCurrent = status === STEP_STATUS.CURRENT;
    const isUpcoming = status === STEP_STATUS.UPCOMING;

    // 1) Build circle classes based on status
    const circleClasses = clsx(
      'flex items-center justify-center rounded-full transition-all duration-300 z-20 text-white',
      buttonClass,
      {
        // Current: blue background, white ring, shadow
        'w-12 h-12 rounded-full bg-custom-blue-deep border-[3px] border-white shadow-[0_0_10px_rgba(0,0,0,0.25)]':
          isCurrent,
        // Upcoming: white background, gray border
        'w-12 h-12 bg-white border-2 border-custom-gray-muted !text-custom-gray-muted': isUpcoming,
        // Completed (but not current): blue background
        'w-12 h-12 bg-custom-blue-deep': status === STEP_STATUS.COMPLETED && !isCurrent,
        // Dim unclickable
        'cursor-not-allowed': !isClickable,
        // Pointer when clickable
        'cursor-pointer': isClickable,
      },
    );

    // 2) Label color: black when current/completed, gray otherwise
    const labelColor =
      isCurrent || status === STEP_STATUS.COMPLETED ? 'text-black' : 'text-custom-gray-muted';

    return (
      <button
        type="button"
        onClick={isClickable ? onClick : undefined}
        disabled={!isClickable}
        aria-current={isCurrent ? 'step' : undefined}
        aria-label={`${label} — ${status}`}
        className="flex flex-col items-center gap-2 focus:outline-none"
      >
        {/* Circle */}
        <div className={circleClasses}>{children}</div>
        {/* Label */}
        <span className={clsx('text-sm font-medium', labelColor, labelClass)}>{label}</span>
      </button>
    );
  },
);

StepButton.propTypes = {
  status: PropTypes.oneOf(Object.values(STEP_STATUS)).isRequired,
  label: PropTypes.string.isRequired,
  isClickable: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
  buttonClass: PropTypes.string,
  labelClass: PropTypes.string,
  children: PropTypes.node,
};

export default StepButton;
